import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, OnInit, Renderer2, ViewChild, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DatePicker } from 'primeng/datepicker';
import { Toast } from 'primeng/toast';
import { filter } from 'rxjs';
import { AppComponent } from 'src/app/app.component';
import { Globals } from 'src/app/globals';
import { SystemBannerService } from 'src/app/layout/services/system-banner.service';
import { GroupLookupValue } from 'src/app/shared/models/lookups';
import { RegionSettings } from 'src/app/shared/models/settings';
import { SystemBanner } from 'src/app/shared/models/system-banner/system-banner.model';
import { GROUP_ICON_MAPPING } from 'src/app/core/constants/group-icon-mapping.constant';
import { AppContextService } from 'src/app/core/services/app-context.service';
import { GlobalSearchService } from 'src/app/core/services/global-search-service';
import { LoaderService } from 'src/app/core/services/loader.service';
import { LookupService } from 'src/app/core/services/lookup.service';
import { ThemeService } from 'src/app/core/services/theme.service';

@Component({
	selector: 'app-main',
	templateUrl: './main.component.html',
	standalone: false,
})
export class MainComponent implements OnInit, AfterViewInit {
	blocked = false;
	@ViewChild('topStack') topStackRef: ElementRef<HTMLElement>;
	@ViewChild('content') contentRef: ElementRef;
	@ViewChild('toast') toastRef: Toast;
	@ViewChild('clearAllToast') clearAllToastRef: Toast;
	topStackHeight = 64;
	showModalDialog: boolean;
	readonly expandedSidebarWidth = 240;
	readonly collapsedSidebarWidth = 80;

	menuClick: boolean;
	menuButtonClick: boolean;
	sidebarActive: boolean;
	mobileMenuActive: boolean;
	menuHoverActive: boolean;
	resetMenu: boolean;

	region: RegionSettings;
	itemGroups: GroupLookupValue[];
	groupIcons = GROUP_ICON_MAPPING.GROUP;
	selectedItems: any[] = [];
	BasePageCSSClass: string;
	sidebarCollapsed = false;
	sidebarHoverActive = false;
	systemBannerMessages: SystemBanner[] = [];
	private readonly destroyRef = inject(DestroyRef);
	private readonly router = inject(Router);
	private readonly globalSearch = inject(GlobalSearchService);
	private lastIsMobileView = false;
	private hoverOpenTimeoutId: number | null = null;
	private readonly mobileBreakpointQuery = '(max-width: 64rem)';
	private mobileMediaQuery: MediaQueryList | null = null;
	private topStackResizeObserver: ResizeObserver | null = null;

	constructor(
		private messageService: MessageService,
		public app: AppComponent,
		public appContext: AppContextService,
		public globals: Globals,
		public loaderService: LoaderService,
		private ref: ChangeDetectorRef,
		private lookupService: LookupService,
		private renderer: Renderer2,
		private el: ElementRef,
		private themeService: ThemeService,
		private systemBannerService: SystemBannerService
	) {
		effect(() => {
			const body = this.el.nativeElement.ownerDocument.body;
			body.className = Array.from(body.classList)
				.filter((className: string) => !className.startsWith('main-'))
				.join(' ');
			const basePageCSSClass = this.appContext.BasePageCSSClass();
			if (basePageCSSClass) {
				this.renderer.addClass(body, basePageCSSClass);
			}
			// Update PrimeNG theme to match the page context (even if empty/default)
			this.themeService.updateThemeForPage(basePageCSSClass || '');
		});
		effect(() => {
			const doc = this.el.nativeElement.ownerDocument;
			const html = doc.documentElement;
			const body = doc.body;
			const locked = this.appContext.BodyScrollLocked();
			const noScrollClass = 'app-body-overflow-hidden';
			if (locked) {
				this.renderer.addClass(html, noScrollClass);
				this.renderer.addClass(body, noScrollClass);
			} else {
				this.renderer.removeClass(html, noScrollClass);
				this.renderer.removeClass(body, noScrollClass);
			}
		});
	}

	ngOnInit(): void {
		this.mobileMediaQuery = window.matchMedia(this.mobileBreakpointQuery);
		this.lastIsMobileView = this.isMobile();
		if (this.lastIsMobileView) {
			this.applyMobileLayoutDefaults();
		}
		this.refreshRegion();
		this.loaderService.blokedChange.subscribe(value => (this.blocked = value));
		this.appContext.userChanged.subscribe(x => {
			this.refreshRegion();
			this.refreshViewByClientCompanies();
		});
		this.showSystemBanner();
		this.router.events
			.pipe(
				filter(event => event instanceof NavigationEnd),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((event: NavigationEnd) => {
				this.showSystemBanner();

				const url = event.urlAfterRedirects ?? event.url;
				const path = url.split('?')[0];
				const resultsRoute = GlobalSearchService.ResultsRoute;
				const onResults = path === resultsRoute || path.startsWith(resultsRoute + '/');
				this.globalSearch.setOnResultsRoute(onResults);

				if (!onResults) {
					this.globalSearch.clear();
				}
			});
	}

	ngAfterViewInit(): void {
		this.refreshViewByClientCompanies();
		this.initializeTopStackObserver();
		this.destroyRef.onDestroy(() => {
			this.topStackResizeObserver?.disconnect();
			this.topStackResizeObserver = null;
		});
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		const isMobileView = this.isMobile();
		if (isMobileView !== this.lastIsMobileView) {
			if (isMobileView) {
				this.applyMobileLayoutDefaults();
			} else {
				this.applyDesktopLayoutDefaults();
			}
			this.lastIsMobileView = isMobileView;
		}

		this.ref.detectChanges();
	}

	refreshRegion() {
		this.region = this.appContext.getRegion();
		let dateFormat = this.region.DateFormat.split('yyyy').join('yy');
		dateFormat = dateFormat.split('M').join('m');
		DatePicker.prototype.getDateFormat = () => dateFormat;
	}

	refreshViewByClientCompanies() {
		if (this.appContext.userHasValidSubscription()) {
			this.lookupService.getClientsCompanies().subscribe(result => {
				const mappedResult = result.map(item => {
					return {
						label: item.GroupLookupTypeCode,
						value: item.GroupLookupTypeName,
						icon: this.groupIcons?.find(x => x.name == item.GroupLookupTypeCode)?.icon,
						items: item.Items.map(subitem => {
							return {
								label: subitem.ExtendedNameValue,
								IsInactive: subitem.ExtendedNameValue == '<No Client Defined>' ? null : subitem.IsInactive,
								value: item.GroupLookupTypeCode + ',' + (subitem.LookupValueId ?? ''),
							};
						}),
					};
				});

				this.itemGroups = JSON.parse(JSON.stringify(mappedResult));

				const dropdown = this.appContext.getSavedValue('ViewBy-Dropdown');
				this.selectedItems = dropdown ? dropdown.split(';').filter(Boolean) : [];
			});
		} else {
			this.itemGroups = [];
			this.selectedItems = [];
		}

		this.ref.detectChanges();
	}

	get pageLevelBlock() {
		return this.blockedElemRef == this.contentRef;
	}

	get blockedElemRef() {
		return this.loaderService.blockedElem ? this.loaderService.blockedElem : this.contentRef;
	}

	get showClearAllToast(): boolean {
		return (this.clearAllToastRef?.messages?.length ?? 0) > 0;
	}

	onClearAll() {
		this.messageService.clear();
	}

	onMsgClose() {
		if (this.toastRef.messages.length === 0) this.messageService.clear();
	}

	onWrapperClick() {
		if (!this.menuClick && !this.menuButtonClick) {
			this.mobileMenuActive = false;
		}

		if (!this.menuClick) {
			this.menuHoverActive = false;
		}

		this.menuClick = false;
		this.menuButtonClick = false;
	}

	onMenuButtonClick(event: Event) {
		this.menuButtonClick = true;

		if (this.isMobile()) {
			this.mobileMenuActive = !this.mobileMenuActive;
		}

		event.preventDefault();
	}

	closeMobileMenu(): void {
		this.mobileMenuActive = false;
	}

	toggleSidebarCollapsed(event?: Event): void {
		if (event) {
			event.stopPropagation();
		}

		if (this.isMobile()) {
			this.sidebarCollapsed = false;
			this.mobileMenuActive = true;
			return;
		}

		this.sidebarCollapsed = !this.sidebarCollapsed;
	}

	onSidebarMouseEnter(): void {
		if (this.sidebarCollapsed) {
			if (this.hoverOpenTimeoutId !== null) {
				window.clearTimeout(this.hoverOpenTimeoutId);
			}
			this.hoverOpenTimeoutId = window.setTimeout(() => {
				this.sidebarHoverActive = true;
				this.hoverOpenTimeoutId = null;
			}, 160);
		}
	}

	onSidebarMouseLeave(): void {
		if (this.sidebarCollapsed) {
			if (this.hoverOpenTimeoutId !== null) {
				window.clearTimeout(this.hoverOpenTimeoutId);
				this.hoverOpenTimeoutId = null;
			}
			this.sidebarHoverActive = false;
		}
	}

	private applyMobileLayoutDefaults(): void {
		this.sidebarCollapsed = false;
		this.sidebarHoverActive = false;
		this.mobileMenuActive = false;
		this.menuHoverActive = false;
		this.menuClick = false;
		this.menuButtonClick = false;
	}

	private applyDesktopLayoutDefaults(): void {
		this.mobileMenuActive = false;
		this.sidebarCollapsed = false;
		this.sidebarHoverActive = false;
		this.menuHoverActive = false;
		this.menuClick = false;
		this.menuButtonClick = false;
	}

	get sidebarWidth(): number {
		if (this.isMobile()) {
			return 0;
		}

		return this.sidebarCollapsed ? this.collapsedSidebarWidth : this.expandedSidebarWidth;
	}

	isDesktopHoverOverlay(): boolean {
		return !this.isMobile() && this.sidebarCollapsed && this.sidebarHoverActive;
	}

	get layoutWidth(): string {
		return this.isMobile() ? '100%' : `calc(100vw - ${this.sidebarWidth}px)`;
	}

	getHeaderStyle(): Record<string, string> {
		return {
			left: this.isMobile() ? '0px' : `${this.sidebarWidth}px`,
			width: this.isMobile() ? '100%' : `calc(100% - ${this.sidebarWidth}px)`,
		};
	}

	onSidebarClick(event: Event) {
		this.menuClick = true;
		this.resetMenu = false;
	}

	isMobile() {
		if (this.mobileMediaQuery) {
			return this.mobileMediaQuery.matches;
		}
		return window.matchMedia(this.mobileBreakpointQuery).matches;
	}

	isOverlay() {
		return this.app.layoutMode === 'overlay';
	}

	showSystemBanner() {
		if (!this.appContext.userHasValidSubscription()) {
			this.systemBannerMessages = [];
			return;
		}
		this.systemBannerService.getSystemBannerMessages().subscribe((sysBannerDto: SystemBanner[]) => {
			this.systemBannerMessages = sysBannerDto ?? [];
			queueMicrotask(() => this.updateTopStackHeight());
		});
	}

	private initializeTopStackObserver(): void {
		if (typeof ResizeObserver === 'undefined' || !this.topStackRef?.nativeElement) {
			return;
		}

		this.updateTopStackHeight();
		this.topStackResizeObserver = new ResizeObserver(() => this.updateTopStackHeight());
		this.topStackResizeObserver.observe(this.topStackRef.nativeElement);
	}

	private updateTopStackHeight(): void {
		const topStackElement = this.topStackRef?.nativeElement;
		if (!topStackElement) {
			this.topStackHeight = 64;
			return;
		}

		const nextHeight = Math.max(64, Math.ceil(topStackElement.getBoundingClientRect().height));
		if (nextHeight !== this.topStackHeight) {
			this.topStackHeight = nextHeight;
			this.ref.detectChanges();
		}
	}
}
